import { batch } from "@suse-o11y/signal";
import {
	FormList,
	FormShape,
	FormSwitch,
	FormUnit,
	type GetFormInputSetter,
	type GetFormOutputVerbose,
} from "@suse-o11y/signal/form";
import { z } from "zod";

import { isStringDirty } from "~/tools/is-string-dirty";
import { Template } from "~/tools/template";
import { Tuple } from "~/tools/tuple";

import { DashboardChartPluginSpec, DashboardWidgetSpec } from "../../common/dashboard-spec";
import { TimeSeriesQuery } from "../../common/dashboard-spec/_time-series-query";
import { FormState as ChartFormState } from "../widget-form/form-state";
import { FormState as QueryFormState } from "../widget-form/query/form-state";

import { FormState as LinkFormState } from "./_link/form-state";

type Spec = DashboardWidgetSpec;

function toInput(spec: undefined | Spec) {
	const [kind, markdown, metrics] =
		spec?.plugin.kind === "Markdown"
			? Tuple("markdown", spec.plugin, undefined)
			: Tuple("metrics", undefined, spec?.plugin);

	return {
		name: spec?.name.source,
		description: spec?.description.source,

		links: spec?.plugin.links.map(LinkFormState.toInput),

		kind: {
			active: kind,
			branches: {
				markdown: markdown?.spec?.text?.source,
				metrics: {
					queries: metrics?.queries.length
						? metrics.queries.map((query) => QueryFormState.fromSpec(TimeSeriesQuery.toQuerySpec(query)))
						: [{ promql: "", alias: "", minStep: "" }],

					chart: ChartFormState.fromSpec(DashboardChartPluginSpec.toChartSpec(metrics)),
				},
			},
		},
	} satisfies GetFormInputSetter<FormState>;
}

type FormState = FormShape<{
	name: FormUnit<string, ReadonlyArray<string>, Template<["${", "}"], null>>;
	description: FormUnit<string, null, Template<["${", "}"], null>>;
	links: FormList<LinkFormState>;
	kind: FormSwitch<
		FormUnit<"markdown" | "metrics">,
		{
			markdown: FormUnit<string, null, Template<["${", "}"], null>>;

			metrics: FormShape<{
				queries: FormList<QueryFormState>;
				chart: ChartFormState;
			}>;
		}
	>;
}>;

function toSpec({ name, description, links, kind }: GetFormOutputVerbose<FormState>): Spec {
	return DashboardWidgetSpec.of({
		name: name ?? Template(""),
		description: description ?? Template(""),
		plugin:
			kind.active === "markdown"
				? {
						kind: "Markdown",
						links: links.map(LinkFormState.toSpec),
						spec: {
							text: kind.branches.markdown ?? Template(""),
						},
					}
				: {
						...DashboardChartPluginSpec.fromChartSpec(ChartFormState.toSpec(kind.branches.metrics.chart)),
						links: links.map(LinkFormState.toSpec),
						queries: kind.branches.metrics.queries.map((query) =>
							TimeSeriesQuery.fromQuerySpec(QueryFormState.toSpec(query)),
						),
					},
	});
}

function init(spec?: Spec): FormState {
	const { name, description, links, kind } = toInput(spec);

	return FormShape({
		name: FormUnit(name ?? "", {
			schema: z
				.string()
				.trim()
				.min(1, "Widget name is required")
				.transform((trimmed) => Template<null>(trimmed)),
			isInputDirty: isStringDirty,
		}),

		description: FormUnit(description ?? "", {
			transform: (input) => Template<null>(input.trim()),
			isInputDirty: isStringDirty,
		}),

		links: FormList(LinkFormState.init, {
			initial: links,
		}),

		kind: FormSwitch(FormUnit(kind.active), {
			markdown: FormUnit(kind.branches.markdown ?? "", {
				isInputDirty: isStringDirty,
				transform: (input) => Template(input.trim()),
			}),

			metrics: FormShape({
				queries: FormList(QueryFormState.init, {
					initial: kind.branches.metrics.queries,
				}),
				chart: ChartFormState.init(kind.branches.metrics.chart),
			}),
		}),
	});
}

/**
 * Overrides the {@link state} input by the incoming {@link spec},
 * resetting the not defined fields to default values.
 */
function override(spec: Spec, state: FormState): void {
	batch(() => {
		// reset
		state.fields.kind.branches.metrics.fields.chart.setInput(ChartFormState.initial);

		// override
		state.setInput(toInput(spec));
	});
}

const FormState = { init, override, toSpec };
export { FormState };
